import { RiskledgerTrackingIdHeader } from '@/api/headerKeys';
import {
  isAuthenticatedByRLRef,
  isEnrolledMfaFactor,
  isSsoSessionRef,
  LogoutReason,
} from './state';
import { markActiveOrg } from './useActiveOrgSync';
import * as cookieAuthApi from './cookieAuthApi';
import useLoginFlowStore from './loginFlowStore';

export { isAuthenticatedByRLRef, LogoutReason };

export enum Mode {
  Cognito = 'cognito',
  SSO = 'sso',
}

let authMode: Mode;

export function setAuthMode(mode: Mode) {
  authMode = mode;
  window.localStorage.setItem('authMode', mode);
}

export function getAuthMode() {
  // Get authMode from localStorage and assign to local variable authMode
  const storedMode = window.localStorage.getItem('authMode');
  if (storedMode && Object.values(Mode).includes(storedMode as Mode)) {
    authMode = storedMode as Mode;
    return authMode;
  }
  // If authMode in localStorage is not valid, set authMode to cognito as default.
  setAuthMode(Mode.Cognito);
  return undefined;
}

// True when the user authenticates with a password we manage in-app (Cognito
// or cookie/BFF), false for SSO where the IdP owns the credential. Drives UI
// that exposes local password / MFA management. Cookie mode reads server truth
// (isSsoSessionRef, set from the resolved session); legacy reads authMode.
export function supportsLocalCredentials() {
  // SSO is server truth from the resolved session (isAuthenticatedByRL sets
  // isSsoSessionRef); SSO users can't manage local passwords/MFA.
  return !isSsoSessionRef.value;
}

// isAuthenticated is called in router.beforeEach so this should always be kept up to date.
const isAuthenticatedRef = ref(false);

// useSession() reads the app QueryClient via inject(), so it must run inside
// vue-router's synchronous injection context, which does NOT survive an `await`.
// Callers must therefore invoke this (and isAuthenticatedByRL) before their first
// await — see routes/guards/auth.ts. The effectScope is a separate concern: it
// owns and disposes the useQuery observer/watchers useSession() spins up, so they
// don't leak once the check resolves.
export async function isAuthenticated() {
  const scope = effectScope();
  try {
    const session = await scope.run(() => cookieAuthApi.useSession().resolvedSession());
    const ok = session?.status === 'active'; // pending + unauthenticated => false
    isAuthenticatedRef.value = ok;
    return ok;
  } catch (e) {
    logError(e);
    isAuthenticatedRef.value = false;
    return false;
  } finally {
    scope.stop();
  }
}

// Logout returns the path to redirect the user to
export async function logout(reason?: LogoutReason) {
  await cookieAuthApi.logout();
  isAuthenticatedRef.value = false;
  isAuthenticatedByRLRef.value = false;
  isSsoSessionRef.value = false;
  sessionStorage.removeItem(RiskledgerTrackingIdHeader);
  useLoginFlowStore().$reset();
  markActiveOrg(null);
  switch (reason) {
    case LogoutReason.AccountLocked:
      return '/auth/login?error=account_locked';
    case LogoutReason.SsoEnforced:
      return '/sso/login?error=sso_enforced';
    default:
      return '/auth/login';
  }
}

// Loop breaker for forced logout-redirects. This guards a local-development
// failure mode: the rl_sid cookie is HttpOnly, so only the backend can clear it
// (via POST /auth/logout), and the browser only honours that clear when the
// Set-Cookie attributes match the original. Locally a developer accumulates
// rl_sid cookies set by differently-configured backends (a wiped local DB,
// dev/staging) whose attributes the current backend's clear can't match, so the
// cookie outlives the logout, the next boot 401s, and we redirect again forever.
// Deployed envs pin Secure/SameSite/Domain per env, so the clear always matches
// and this can't happen — but the breadcrumb below is cheap insurance, so we
// don't gate it on the env and risk masking a future regression in production.
const FORCED_LOGOUT_KEY = 'auth:forcedLogoutAt';
const FORCED_LOGOUT_LOOP_WINDOW_MS = 5000;

function readForcedLogoutAt(): number {
  try {
    return Number(window.sessionStorage.getItem(FORCED_LOGOUT_KEY)) || 0;
  } catch {
    // A breadcrumb we can't read can't break the loop; degrade to the old
    // always-redirect behaviour rather than throw.
    return 0;
  }
}

function writeForcedLogoutAt(value: number | null): void {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(FORCED_LOGOUT_KEY);
    } else {
      window.sessionStorage.setItem(FORCED_LOGOUT_KEY, String(value));
    }
  } catch {
    // See readForcedLogoutAt: best-effort, never block logout on storage.
  }
}

// Cleared once a session verifies as active so a normal logout much later in the
// session isn't misread as the tail of an earlier loop.
export function clearForcedLogoutBreadcrumb(): void {
  writeForcedLogoutAt(null);
}

// Single source of truth for "the current URL is an unauthenticated /auth page"
// (login, MFA setup, etc.), shared by fetchClient so the loop-breaking guard
// reads the route the same way everywhere.
export function isOnAuthRoute(): boolean {
  return window.location.pathname.startsWith('/auth');
}

// Logs out and hard-redirects. If we forced the same redirect moments ago we're
// evidently back because the cookie was never cleared, so re-redirecting would
// just reload into the same 401 — settle on login instead of bouncing.
async function logoutAndRedirect(reason?: LogoutReason): Promise<void> {
  const path = await logout(reason);

  const last = readForcedLogoutAt();
  const now = Date.now();
  const looping = last > 0 && now - last < FORCED_LOGOUT_LOOP_WINDOW_MS;

  if (looping) {
    writeForcedLogoutAt(null);
    // Already where an unauthenticated user belongs; another reload changes
    // nothing, so only navigate if we're somewhere else. The error code tells
    // the user why they're back at login (the loop swallowed their session)
    // rather than dumping them on a bare form.
    if (!isOnAuthRoute()) {
      window.location.href = '/auth/login?error=session_expired';
    }
    return;
  }

  writeForcedLogoutAt(now);
  window.location.href = path;
}

// An expired session 401s every in-flight request at once, and each one calls
// forceLogoutRedirect. Concurrent callers share the first call: otherwise each
// POSTs its own logout, and then the first redirect cancels the rest, which
// adds Network error noise in sentry.
let forcedLogoutInFlight: Promise<void> | undefined;

export function forceLogoutRedirect(reason?: LogoutReason): Promise<void> {
  forcedLogoutInFlight ??= logoutAndRedirect(reason).finally(() => {
    forcedLogoutInFlight = undefined;
  });
  return forcedLogoutInFlight;
}

// A persistently failing RL auth check (e.g. the backend can't resolve the user
// for a deleted-but-cached session) means the session is effectively invalid.
// After this many consecutive errors we stop retrying and log the user out,
// rather than letting every navigation re-hit the failing endpoint forever.
let consecutiveCheckFailures = 0;
const MAX_CHECK_FAILURES = 3;

// this includes any extra checks RL applies on top of cognito and sso
// authentication, such as MFA preference
export async function isAuthenticatedByRL() {
  // resolvedSession() rejects on a backend failure but resolves to 'unauthenticated'
  // on a 401, so the circuit-breaker below only counts genuine errors. Same
  // injection-context rule and effectScope disposal as isAuthenticated(): call
  // before any await, and dispose the useQuery observer once resolved.
  const scope = effectScope();
  try {
    const session = await scope.run(() => cookieAuthApi.useSession().resolvedSession());
    // Reached the backend and got an answer — the session is verifiable.
    consecutiveCheckFailures = 0;
    // Returning true for an active session without an enrolled factor would
    // bounce the just-signed-up user away from /auth/mfa/setup before they
    // can enrol.
    //
    // SSO is the exception: the IdP owns MFA, so SSO users have no RL-side
    // preference and the gate would wrongly strand them on /auth/mfa/setup.
    // Read the SSO fact from the session (server truth) rather than a
    // localStorage flag, which drifts from the cookie session and left
    // returning SSO users stranded (RL-7966 / FEDX-159).
    const isActive = session?.status === 'active';
    const isSso = isActive ? session.sso : false;
    isSsoSessionRef.value = isSso;
    const ok = isActive && (isSso || isEnrolledMfaFactor(session.user.mfaPreference));
    isAuthenticatedRef.value = isActive;
    isAuthenticatedByRLRef.value = ok;
    // A verified-active session means we're not in a logout loop; drop any
    // breadcrumb so a genuine later logout redirects normally.
    if (isActive) clearForcedLogoutBreadcrumb();
    return ok;
  } catch (e) {
    isAuthenticatedByRLRef.value = false;
    logError(e);
    consecutiveCheckFailures += 1;
    if (consecutiveCheckFailures >= MAX_CHECK_FAILURES) {
      consecutiveCheckFailures = 0;
      await forceLogoutRedirect(LogoutReason.SessionExpired);
    }
    return false;
  } finally {
    scope.stop();
  }
}
